import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { FeatureStatus } from '@/components/feature/FeatureStatus';
import { usePlanLimitStore, closePlanLimit } from '@/stores/planLimitStore';
import { formatNumber } from '@/lib/utils';

// Recursos con cuota (contrato backend `error.details.resource`). Cualquier otro
// valor cae en `generic` para no romper si el backend añade un recurso nuevo.
// `ai_per_month` y `ai_monthly_cost_eur` son los dos topes de IA. Faltaban: el
// backend ya los mandaba como límite alcanzado, pero al no estar aquí caían al
// copy `generic` — y encima no llegaban, porque el error de ARIA viaja por SSE y
// no por el MutationCache. Las dos mitades se arreglan a la vez o no se nota.
//
// `ai_credits` sustituye a `ai_per_month`: el backend dejó de contar MENSAJES y
// pasó a un saldo anclado al coste real, porque un turno de fan-out con Sonnet
// consume ~50x uno corto con Haiku y cobrar lo mismo por los dos no significaba
// nada. `ai_per_month` se deja en la lista a propósito hasta que prod despliegue:
// mientras haya una versión antigua del backend viva, seguirá mandando ese
// recurso, y sacarlo de aquí lo tiraría al copy genérico.
//
const KNOWN_RESOURCES = new Set([
  'invoices_per_year', 'ocr_per_month', 'banks', 'seats',
  'ai_credits', 'ai_per_month', 'ai_monthly_cost_eur',
]);

/**
 * Modal de upsell que se abre al superar una cuota del plan (`PLAN_QUOTA_EXCEEDED`).
 * Se monta UNA vez en `App.jsx`; lo dispara el MutationCache global vía el store
 * (`openPlanLimit`), así que todos los puntos de mutación (factura, OCR, banco,
 * invitar) quedan cubiertos sin tocarlos uno a uno.
 *
 * La CTA navega a la pestaña Plan de Configuración. Como el modal vive fuera del
 * Router (App.jsx monta antes que el BrowserRouter de `pages/index.jsx`), la
 * navegación es un enlace real (`href`) en vez de `useNavigate`.
 */
export default function PlanLimitModal() {
  const { t } = useTranslation();
  const { open, details } = usePlanLimitStore();

  const resource = details?.resource;
  const copyKey = resource && KNOWN_RESOURCES.has(resource) ? resource : 'generic';

  const title = t(`plan.limit_modal.${copyKey}.title`);
  const baseDescription = t(`plan.limit_modal.${copyKey}.description`);

  // Línea de uso ("Llevas X de Y") solo si el backend manda usage+limit numéricos.
  const usage = details?.usage;
  const limit = details?.limit;
  // Con el saldo a cero el backend manda `limit: 0`, y «Llevas 0 de 0» no dice
  // nada: en ese caso se deja solo la descripción, que ya explica qué pasa.
  const showUsage = typeof usage === 'number' && typeof limit === 'number' && limit > 0;
  const usageLine = showUsage
    ? t('plan.limit_modal.usage_line', {
        used: formatNumber(usage),
        limit: formatNumber(limit),
      })
    : null;

  const description = usageLine ? `${baseDescription} ${usageLine}` : baseDescription;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) closePlanLimit(); }}>
      <DialogContent size="lg" className="p-0">
        {/* Título/descripción accesibles para Radix (evita el warning de a11y);
            la copia visible la pinta FeatureStatus abajo. */}
        <DialogHeader className="sr-only">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <FeatureStatus
          kind="upgrade_required"
          feature={`plan.limit.${copyKey}`}
          variant="section"
          title={title}
          description={description}
          cta={{ label: t('plan.limit_modal.cta'), href: '/configuracion?section=plan' }}
        />
      </DialogContent>
    </Dialog>
  );
}
